/**
 * Integration Store
 *
 * Centralized reactive state for integration status.
 * Exposes `isApolloEnabled` so any view can check without fetching.
 * Data is loaded once on authentication and refreshed explicitly when
 * an admin enables/disables an integration.
 */

import { defineStore, acceptHMRUpdate } from 'pinia'
import { ref, computed } from 'vue'
import { integrationService } from '@/modules/integrations/api/service'
import type { IntegrationStatus } from '@/modules/integrations/api/types'

export const useIntegrationStore = defineStore('integrations', () => {
    const integrations = ref<IntegrationStatus[]>([])
    const isLoaded = ref(false)

    const apolloIntegration = computed(() =>
        integrations.value.find(i => i.system === 'apollo') ?? null,
    )

    const isApolloEnabled = computed(() =>
        apolloIntegration.value?.enabled === true,
    )

    const xweatherIntegration = computed(() =>
        integrations.value.find(i => i.system === 'xweather') ?? null,
    )

    const isXweatherEnabled = computed(() =>
        xweatherIntegration.value?.enabled === true,
    )

    const companycamIntegration = computed(() =>
        integrations.value.find(i => i.system === 'companycam') ?? null,
    )

    const isCompanyCamEnabled = computed(() =>
        companycamIntegration.value?.enabled === true,
    )

    const acumaticaIntegration = computed(() =>
        integrations.value.find(i => i.system === 'acumatica') ?? null,
    )

    const isAcumaticaEnabled = computed(() =>
        acumaticaIntegration.value?.enabled === true,
    )

    async function loadIntegrations() {
        try {
            const resp = await integrationService.listStatus()
            integrations.value = resp.data
        } catch {
            // Non-critical — integrations may not exist yet
            integrations.value = []
        } finally {
            isLoaded.value = true
        }
    }

    /** Re-fetch after enabling/disabling an integration */
    async function refresh() {
        await loadIntegrations()
    }

    return {
        integrations,
        isLoaded,
        apolloIntegration,
        isApolloEnabled,
        xweatherIntegration,
        isXweatherEnabled,
        companycamIntegration,
        isCompanyCamEnabled,
        acumaticaIntegration,
        isAcumaticaEnabled,
        loadIntegrations,
        refresh,
    }
})

// Preserve integration state during Vite HMR (dev-only)
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useIntegrationStore, import.meta.hot))
}
